import Fuse from "fuse.js";
import { getPages } from "./siteIndex";

// Instant, offline site search.
//
// Replaces the previous embedding pipeline, which downloaded a ~23MB English
// sentence encoder to rank 8 Turkish pages. For a set this small, weighted
// lexical matching is both faster (sub-millisecond, no network) and more
// accurate for Turkish than an English model.
//
// Ranking has three ingredients:
//   1. IDF   — a word that appears on only one page ("işveren") outranks one
//              that appears on several ("hizmet"). Without this, "İşveren
//              hizmetleri nelerdir?" ties and resolves by array order.
//   2. Prefix matching — Turkish is agglutinative, so "şirketim" has to match
//              the term "şirket" and "hizmetleri" the term "hizmetler".
//   3. Phrase hits — a whole multi-word term appearing in the query is the
//              strongest signal available and is scored on top.

const FOLD = {
  ç: "c", ğ: "g", ı: "i", ö: "o", ş: "s", ü: "u", â: "a", î: "i", û: "u",
};

// Turkish-aware folding. Two separate problems:
//   1. Casing: JS toLowerCase() maps "İ" to "i" + a combining dot, and leaves
//      "I" as "i" rather than "ı". toLocaleLowerCase("tr") gets both right.
//   2. Diacritics: people type "iletisim" for "iletişim", so both the query and
//      the index are folded to a bare-ASCII skeleton before comparison.
export function normalize(text = "") {
  return text
    .toLocaleLowerCase("tr")
    .replace(/[çğıöşüâîû]/g, (c) => FOLD[c] || c)
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// Filler words that carry no routing signal. Kept deliberately short: every
// word removed here is a word that can no longer disambiguate a page.
const STOP = new Set([
  // tr
  "bir", "bu", "su", "ve", "ile", "icin", "ama", "veya", "nedir", "nelerdir",
  "hangi", "bana", "beni", "benim", "sizin", "siz", "ben", "bilgi", "almak",
  "istiyorum", "istiyoruz", "isterim", "istiyor", "lutfen", "merhaba", "var",
  "yok", "daha", "cok", "sey", "olan", "nasil", "hakkinda", "misiniz", "musunuz",
  // en
  "the", "and", "for", "you", "your", "what", "how", "can", "want", "need",
  "tell", "with", "that", "this", "are", "does", "get", "from", "about",
]);

function tokenize(normText) {
  return normText.split(" ").filter((t) => t.length >= 2 && !STOP.has(t));
}

// A query token matches an index token when either is a prefix of the other.
// Short tokens ("cv", "ik") must match exactly so they don't over-trigger.
function tokensMatch(qTok, iTok) {
  if (qTok === iTok) return true;
  const min = Math.min(qTok.length, iTok.length);
  if (min < 4) return false;
  return qTok.startsWith(iTok) || iTok.startsWith(qTok);
}

const _cache = new Map();

function getEngine(locale) {
  if (_cache.has(locale)) return _cache.get(locale);

  const entries = getPages(locale).map((p) => {
    const terms = p.terms.map(normalize);
    const phrases = terms.filter((t) => t.includes(" "));
    const tokenSet = new Set();
    for (const t of [...terms, normalize(p.title)]) {
      for (const tok of tokenize(t)) tokenSet.add(tok);
    }
    return {
      ...p,
      _terms: terms,
      _phrases: phrases,
      _tokens: [...tokenSet],
      _search: [normalize(p.title), ...terms].join(" "),
    };
  });

  // Document frequency per token, for IDF.
  const df = new Map();
  for (const e of entries) {
    for (const tok of e._tokens) df.set(tok, (df.get(tok) || 0) + 1);
  }
  const N = entries.length;
  const idf = (tok) => Math.log(1 + N / (df.get(tok) || 0.4));

  const fuse = new Fuse(entries, {
    includeScore: true,
    ignoreLocation: true,
    threshold: 0.34,
    minMatchCharLength: 3,
    keys: ["_search"],
  });

  const engine = { entries, idf, fuse };
  _cache.set(locale, engine);
  return engine;
}

function scoreEntry(entry, queryNorm, qTokens, idf) {
  // Phrase hits: a full multi-word term present in the query.
  let phraseChars = 0;
  for (const phrase of entry._phrases) {
    if (queryNorm.includes(phrase)) phraseChars = Math.max(phraseChars, phrase.length);
  }
  // A single-word term typed verbatim also counts as a phrase-strength hit.
  for (const term of entry._terms) {
    if (!term.includes(" ") && term.length >= 4 && queryNorm.includes(term)) {
      phraseChars = Math.max(phraseChars, term.length);
    }
  }
  const phraseScore = phraseChars
    ? Math.min(1, phraseChars / Math.max(queryNorm.length, 1)) * 0.5 + 0.45
    : 0;

  // IDF-weighted token overlap.
  let matched = 0;
  let total = 0;
  for (const qTok of qTokens) {
    const hit = entry._tokens.find((iTok) => tokensMatch(qTok, iTok));
    const weight = idf(hit || qTok);
    total += weight;
    if (hit) matched += weight;
  }
  const tokenScore = total > 0 ? matched / total : 0;

  if (queryNorm && entry._terms.includes(queryNorm)) return 1;
  return Math.max(phraseScore, tokenScore * 0.95);
}

/**
 * Search the site.
 * @returns {{results: Array, confidence: number, query: string}}
 *   results   ranked destinations, each with a 0..1 `score`
 *   confidence score of the best hit (0 when nothing matched)
 */
export function searchSite(query, locale = "tr", k = 4) {
  const queryNorm = normalize(query);
  if (!queryNorm) return { results: [], confidence: 0, query: queryNorm };

  const { entries, idf, fuse } = getEngine(locale);
  const qTokens = tokenize(queryNorm);

  // Fuse only supplies typo tolerance; it is damped so a fuzzy-only match can
  // surface as a suggestion but never auto-route.
  const fuzzy = new Map();
  for (const hit of fuse.search(queryNorm)) {
    fuzzy.set(hit.item.id, (1 - (hit.score ?? 1)) * 0.72);
  }

  const scored = entries
    .map((entry) => {
      const score = Math.max(
        scoreEntry(entry, queryNorm, qTokens, idf),
        fuzzy.get(entry.id) || 0
      );
      const { _terms, _phrases, _tokens, _search, terms, ...page } = entry;
      return { ...page, score };
    })
    .filter((r) => r.score > 0.25)
    .sort((a, b) => b.score - a.score);

  return {
    results: scored.slice(0, k),
    confidence: scored[0]?.score || 0,
    query: queryNorm,
  };
}

// Above this the match is unambiguous enough to send someone straight there.
export const AUTO_ROUTE = 0.82;
